"use client";

import classNames from "classnames";
import Cookies from "js-cookie";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { APRIL_FOOLS_SOLUTION } from "./aprilFools";
import { AprilFoolsKeyboard } from "./AprilFoolsKeyboard";
import { COOKIE_KEY, CookieState } from "./cookies";
import { formatTimeInSeconds } from "./formatTimeInSeconds";
import { getSolveTimeEmoji } from "./getSolveTimeEmoji";
import { Header } from "./Header";
import { InfoPopup } from "./InfoPopup";
import { useSolveClock } from "./useSolveClock";
import { WordleGuess } from "./WordleGuess";

export declare namespace AprilFoolsGame {
  export interface Props {
    puzzleIndex: number;
    initialGuess: string | undefined;
    doNotUseCookie?: boolean;
    yesterdaysAnswer: string;
    initialSolveTime?: number;
  }
}

// Valid characters for April Fools mode
const APRIL_FOOLS_CHARS = ["◕", "‿", "◔", "ಠ", "ಥ", "ʘ", "ಸ", "ಿ", "ಡ", "ಇ", "(", ")", "¯", "\\", "_", "/", "ツ", "♪"];

// Fun first row that looks like it could lead to the solution
const APRIL_FOOLS_FIRST_ROW = "‿◕◔♪(";
const APRIL_FOOLS_FIRST_ROW_RESULT = "BGBBY";

interface State {
  currentGuess: string;
  didWin: boolean;
  shouldShakeGuess: boolean;
}

export function AprilFoolsGame({
  puzzleIndex,
  initialGuess = "",
  yesterdaysAnswer,
  doNotUseCookie = false,
  initialSolveTime,
}: AprilFoolsGame.Props) {
  const posthog = usePostHog();

  const solution = APRIL_FOOLS_SOLUTION;

  const isCorrectSolution = useCallback(
    (guess: string) => {
      return guess.toLowerCase() === solution.toLowerCase();
    },
    [solution]
  );

  const [{ currentGuess, didWin, shouldShakeGuess }, setState] =
    useState<State>({
      currentGuess: initialGuess,
      didWin: isCorrectSolution(initialGuess),
      shouldShakeGuess: false,
    });

  const [showInfoPopup, setShowInfoPopup] = useState(false);

  const { timeInSeconds } = useSolveClock({
    hasSolved: didWin,
    initialTimeInSeconds: initialSolveTime ?? 0,
  });

  const canAddLetter = currentGuess.length < 5 && !didWin;

  const onPressLetter = useCallback(
    (letter: string) => {
      if (!canAddLetter) {
        return;
      }

      const newGuess = currentGuess + letter;

      setState((prevState) => ({
        ...prevState,
        currentGuess: newGuess,
        shouldShakeGuess: false,
      }));

      // Auto-win if word is complete and correct
      if (newGuess.length === 5 && isCorrectSolution(newGuess)) {
        setState((prevState) => ({
          ...prevState,
          currentGuess: newGuess,
          didWin: true,
        }));
        posthog?.capture("solved", {
          puzzleIndex,
          solution: APRIL_FOOLS_SOLUTION,
          timeInSeconds,
        });
      }
    },
    [canAddLetter, currentGuess, isCorrectSolution, posthog, puzzleIndex, timeInSeconds]
  );

  const onPressBackspace = useCallback(() => {
    setShouldShowFailureReason(false);
    setState((prevState) => ({
      ...prevState,
      currentGuess: prevState.currentGuess.slice(0, -1),
      shouldShakeGuess: false,
    }));
  }, []);

  const [failureReason, setFailureReason] = useState<string>("");
  const [shouldShowFailureReason, setShouldShowFailureReason] = useState(false);

  const commitGuess = useCallback(() => {
    if (currentGuess.length !== 5) {
      return;
    }

    const isCorrect = isCorrectSolution(currentGuess);

    if (isCorrect) {
      setState((prevState) => ({
        ...prevState,
        didWin: true,
      }));
      posthog?.capture("solved", {
        puzzleIndex,
        solution: APRIL_FOOLS_SOLUTION,
        timeInSeconds,
      });
    } else {
      if (shakeTimeoutRef.current != null) {
        clearTimeout(shakeTimeoutRef.current);
      }
      setState((prevState) => ({
        ...prevState,
        shouldShakeGuess: true,
      }));
      setFailureReason("Nope");
      setShouldShowFailureReason(true);
    }
  }, [
    currentGuess,
    isCorrectSolution,
    posthog,
    puzzleIndex,
    timeInSeconds,
  ]);

  const shakeTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    if (shouldShakeGuess) {
      if (shakeTimeoutRef.current != null) {
        clearTimeout(shakeTimeoutRef.current);
      }
      shakeTimeoutRef.current = setTimeout(() => {
        setState((prevState) => ({
          ...prevState,
          shouldShakeGuess: false,
        }));
      }, 750);
      return () => {
        if (shakeTimeoutRef.current != null) {
          clearTimeout(shakeTimeoutRef.current);
        }
      };
    }
  }, [shouldShakeGuess]);

  useEffect(() => {
    if (shouldShowFailureReason) {
      const timeout = setTimeout(() => {
        setShouldShowFailureReason(false);
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [shouldShowFailureReason]);

  useEffect(() => {
    if (didWin) {
      return;
    }

    const keyboardListener = (event: KeyboardEvent) => {
      const key = event.key;
      if (canAddLetter && !event.metaKey && APRIL_FOOLS_CHARS.includes(key)) {
        onPressLetter(key);
      }
      if (key === "Backspace") {
        onPressBackspace();
      }
      if (key === "Enter" && currentGuess.length === 5) {
        commitGuess();
      }
    };

    document.addEventListener("keydown", keyboardListener);
    return () => {
      document.removeEventListener("keydown", keyboardListener);
    };
  }, [
    canAddLetter,
    onPressLetter,
    onPressBackspace,
    currentGuess.length,
    commitGuess,
    didWin,
    initialSolveTime,
  ]);

  const shareText = useMemo(() => {
    if (!didWin) {
      return "";
    }

    const { minutes, seconds } = formatTimeInSeconds(timeInSeconds);
    const solveTimeEmoji = getSolveTimeEmoji(timeInSeconds);
    
    let text = `Word in 2 #${puzzleIndex + 1}\n\n${APRIL_FOOLS_FIRST_ROW_RESULT.split("").map(char => 
      char === "G" ? "🟩" : char === "Y" ? "🟨" : "⬛"
    ).join("")}`;
    
    text += `\n🟩🟩🟩🟩🟩\n${solveTimeEmoji} ${minutes}:${seconds}\n\nhttps://wordin2.com`;
    
    return text;
  }, [didWin, timeInSeconds, puzzleIndex]);

  const shouldCopyPaste = useMemo(() => !isMobile(), []);

  const copyShareText = useCallback(async () => {
    posthog?.capture("shared");

    if (typeof navigator === "undefined") {
      return;
    }

    if (shouldCopyPaste) {
      navigator.clipboard.writeText(shareText);
      alert("Copied to clipboard");
    } else if (navigator.share != null) {
      await navigator.share({
        title: "Word in 2",
        text: shareText,
      });
    }
  }, [shareText, shouldCopyPaste, posthog]);

  useEffect(() => {
    if (!doNotUseCookie) {
      setCookieState({
        puzzleIndex,
        currentGuess,
        time: timeInSeconds,
      });
    }
  }, [currentGuess, timeInSeconds, doNotUseCookie, puzzleIndex]);

  // Clock starts automatically in useSolveClock when hasSolved is false

  const hasStartedRef = useRef(false);
  useEffect(() => {
    if (currentGuess.length > 0 && !hasStartedRef.current) {
      hasStartedRef.current = true;
      posthog?.capture("started", {
        puzzleIndex,
      });
    }
  }, [currentGuess.length, posthog, puzzleIndex]);

  return (
    <div className="flex flex-col flex-1 relative">
      {showInfoPopup && (
        <InfoPopup
          close={() => setShowInfoPopup(false)}
          yesterdaysAnswer={yesterdaysAnswer}
        />
      )}
      <Header
        onClickInfo={() => setShowInfoPopup(true)}
        timerInSeconds={timeInSeconds}
      />
      <div className="flex flex-col items-center">
        <div className="text-4xl font-bold font-serif mt-5 mb-10">Word in 2</div>
      </div>
      <div className="flex flex-col max-w-[500px] mx-auto w-full flex-1">
        <div className="flex flex-col flex-1">
          <div className="flex flex-col gap-2">
            <WordleGuess
              word={APRIL_FOOLS_FIRST_ROW}
              result={APRIL_FOOLS_FIRST_ROW_RESULT}
              colorBorderOnly
            />
            <WordleGuess
              word={currentGuess}
              result={didWin ? "GGGGG" : undefined}
              shake={shouldShakeGuess}
              colorBorderOnly={false}
            />
          </div>

          <div className="flex flex-1 min-w-0 px-2 mb-5">
            {didWin ? (
              <div className="flex flex-1 justify-center items-center flex-col gap-4">
                <div className="text-3xl font-serif">Well done!</div>
                <button
                  className="m-3 bg-[rgb(97,140,85)] hover:bg-[rgb(86,124,75)] active:bg-[rgb(75,108,66)] px-10 py-4 rounded text-2xl select-none cursor-pointer font-extrabold"
                  onClick={copyShareText}
                >
                  SHARE
                </button>
              </div>
            ) : (
              <div className="flex-1 flex flex-col">
                <div className="flex flex-1 justify-center items-center">
                  <div
                    className={classNames(
                      "bg-gray-400/15 rounded py-1 px-2 transition",
                      shouldShowFailureReason ? "opacity-100" : "opacity-0"
                    )}
                  >
                    {failureReason}
                  </div>
                </div>
                <AprilFoolsKeyboard
                  onPressBackspace={
                    currentGuess.length > 0 ? onPressBackspace : undefined
                  }
                  onPressCharacter={canAddLetter ? onPressLetter : undefined}
                  onPressEnter={
                    currentGuess.length === 5 ? commitGuess : undefined
                  }
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const MOBILE_USER_AGENT_REGEX =
  /iPhone|iPad|iPod|Android|webOS|BlackBerry|Windows Phone|Opera Mini|IEMobile/i;

function isMobile() {
  return (
    typeof navigator !== "undefined" &&
    MOBILE_USER_AGENT_REGEX.test(navigator.userAgent)
  );
}

function setCookieState(state: CookieState) {
  Cookies.set(COOKIE_KEY, JSON.stringify(state), {
    // 2 days
    expires: 2,
  });
}